import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {AbstractFormFieldComponent} from "../abstract-form-field.component";
import {NgbPopover, NgbTooltip, NgbTypeahead} from "@ng-bootstrap/ng-bootstrap";
import {TranslatePipe} from "@ngx-translate/core";
import {catchError, debounceTime, delay, distinctUntilChanged, merge, Observable, of, OperatorFunction, Subject, Subscription, tap} from "rxjs";
import {filter, map, switchMap} from "rxjs/operators";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {NgStyle} from "@angular/common";
import {isBlank, isDefined, isUuid, nonBlank, unsubscribe} from "../../utils/epic.utils";
import {FormWrapper} from "../items/form.wrapper";
import {FormFieldErrorComponent} from "../form-field-error/form-field-error.component";
import {FormSelectOption} from "../items/form-select-option";
import {IconComponent} from "../../icon/icon.component";
import {FormLabelComponent} from "../form-label/form-label.component";
import {FormTypeaheadConfig} from "./form-typeahead.config";
import {Size} from "../../../base/data/size";
import {SafeHtmlPipe} from "../../pipes/safe-html.pipe";
import {FormValueChangeItem} from "../items/form-value-change.item";

/**
 * see https://ng-bootstrap.github.io/#/components/typeahead/examples
 */
@Component({
  selector: 'acc-form-typeahead',
  imports: [
    TranslatePipe,
    FormsModule,
    NgbTypeahead,
    ReactiveFormsModule,
    FormFieldErrorComponent,
    IconComponent,
    NgbPopover,
    FormLabelComponent,
    NgStyle,
    NgbTooltip,
    SafeHtmlPipe
  ],
  templateUrl: './form-typeahead.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-typeahead.component.css'
})
export class FormTypeaheadComponent extends AbstractFormFieldComponent implements OnInit, OnDestroy {

  @ViewChild('input')
  public input: ElementRef | undefined;

  @ViewChild('instance', {static: true})
  public instance: NgbTypeahead | undefined;

  @Input({required: true})
  public name!: string;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input({required: true})
  public type: 'select' | 'input' = 'select';

  @Input()
  public changeable: boolean = true;

  @Input()
  public config: FormTypeaheadConfig = new FormTypeaheadConfig();

  @Output()
  public accChanged: EventEmitter<FormSelectOption | undefined> = new EventEmitter<FormSelectOption | undefined>();

  protected readonly focusTypeahead: Subject<string> = new Subject<string>();
  protected readonly clickTypeahead: Subject<string> = new Subject<string>();
  protected readonly changeTypeahead: Subject<string> = new Subject<string>();
  protected readonly selectTypeahead: Subject<FormSelectOption> = new Subject<FormSelectOption>();
  protected search: OperatorFunction<string, readonly any[]>;
  protected formatter: (item: any) => string;
  protected model: string | FormSelectOption | undefined;
  protected showCleanAction: boolean = false;
  protected searching: boolean = false;

  private readonly subs: Subscription[] = [];
  private readonly cleanActionWidthRem: number = 3;
  private data: FormSelectOption[] | undefined = undefined;
  private isAsyncData: boolean = false;
  private asyncDataFn: ((input: any) => Observable<FormSelectOption[]>) | undefined;
  private timeout: any;

  constructor() {
    super();
    this.search = this.buildSearch();
    this.formatter = this.buildFormatter();
  }

  ngOnInit(): void {
    this.subs.push(
      this.focusTypeahead.subscribe(() => this._onTouch(this.name, this.wrapper)),
      this.clickTypeahead.subscribe(() => this._onTouch(this.name, this.wrapper)),
      this.changeTypeahead.subscribe(() => this.onSelectChange(this.model)),
      this.selectTypeahead.subscribe((data) => this.onSelectChange(data)),
      this.wrapper.onReady().pipe(filter((ready) => ready)).subscribe(() => this.setInitValue()),
      this.wrapper.onChangeByNameDistinctValue(this.name).subscribe((v) => this.selectOnExternalChange(v))
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  private selectOnExternalChange(value: FormValueChangeItem): void {
    const option: FormSelectOption | undefined = this.data?.find((o) => o.value === value.getValue());
    if (isDefined(option)) {
      this.model = option;
      this.onSelectChange(option);
    }
  }

  protected override getSize(): Size | undefined {
    return this.config.size;
  }

  public cleanValue(): void {
    const oldValue: any = this.wrapper.getValue(this.name);
    if (nonBlank(oldValue)) {
      this._onChangeValue(this.name, this.wrapper, '', oldValue);
    }
    this.showCleanAction = false;
    this.model = undefined;
    this.accChanged.emit(undefined);
  }

  public getCurrentValue(): string | FormSelectOption | undefined {
    return this.model;
  }

  public getCurrentValueAsString(): string | undefined {
    const value: FormSelectOption | string | undefined = this.getCurrentValue();
    if (typeof value === 'string') {
      return value;
    } else if (value instanceof FormSelectOption) {
      return value.value;
    }
    return undefined;
  }

  public setInitValue(): Promise<string | FormSelectOption | undefined> {
    this.isAsyncData = this.wrapper.hasAsyncSelectOptions(this.name);
    this.asyncDataFn = this.wrapper.getAsyncSelectOptions(this.name);

    return new Promise((resolve) => {
      this.data = this.wrapper.getSelectOptions(this.name);

      if (this.wrapper.hasControl(this.name)) {
        // Set translations if data is synchronized
        if (isDefined(this.data) && !this.isAsyncData) {
          this.data.filter((d) => d.hasTranslation())
            .forEach((d) => d.translatedValue = this.i18n.translate(d.translation));
        }

        let selectedId: any = this.wrapper.getValue(this.name);
        if (!selectedId && this.wrapper.getDefaultOption(this.name)) {
          selectedId = this.wrapper.getDefaultOption(this.name)?.value;
        }
        const selectedData: FormSelectOption | undefined = this.data.find((d) => d.value === selectedId);
        if (isDefined(selectedData)) {
          this.model = selectedData;
          this.showCleanAction = nonBlank(this.model.value);
          resolve(this.model);
        }
        // Value is a UUID and can be resolved explicitly by its id.
        // This avoids the pitfall that the selected option is not part of the (limited) async result list.
        else if (isUuid(selectedId) && this.wrapper.hasAsyncSelectOptionById(this.name)) {
          this.subs.push(this.wrapper.getAsyncSelectOptionById(this.name)!(selectedId).subscribe((o) => {
            if (isDefined(o)) {
              this.model = o;
              this.showCleanAction = nonBlank(this.model.value);
            }
            resolve(this.model);
          }));
        }
        // Workaround: AsyncData is set and Value is a UUID
        else if (isUuid(selectedId) && isDefined(this.asyncDataFn)) {
          this.subs.push(this.asyncDataFn('').subscribe((res) => {
            const option: FormSelectOption | undefined = res.find((o) => o.value === selectedId);
            if (isDefined(option)) {
              this.model = option;
              this.showCleanAction = nonBlank(this.model.value);
              resolve(this.model);
            }
          }));
        }
        // Type: Input
        else if (this.type === 'input') {
          this.model = new FormSelectOption(selectedId, selectedId);
          this.showCleanAction = nonBlank(this.model.value);
          resolve(this.model);
        } else {
          resolve(undefined);
        }
      } else {
        resolve(undefined);
      }
    });
  }

  protected override isViewOnly(): boolean {
    return this._isViewOnly(this.name, this.wrapper);
  }

  protected getDataIdForResult(data: FormSelectOption): string {
    return this.name + '_' + data.value;
  }

  protected isInvalid(): boolean {
    return this._isInvalid(this.name, this.wrapper);
  }

  protected getPlaceholder(): string {
    if (this.isViewOnly()) {
      return '';
    } else if (isDefined(this.config.placeholder)) {
      return this.config.placeholder;
    } else if (this.type === 'input') {
      return 'form.select.inputPrompt';
    }
    return 'form.select.selectionPrompt';
  }

  protected getClass(): string {
    const size: string = isDefined(this.getSize()) ? 'form-control-' + this.getSize()?.value : '';
    const back: string = nonBlank(this.config.background) ? 'bg-' + this.config.background : '';
    return size + ' ' + back;
  }

  protected onInputChange(event: Event): void {
    if (isDefined(event.target) && event.target instanceof HTMLInputElement) {
      this.changeTypeahead.next(event.target.value);
    }
  }

  protected getStyle(): Record<string, string> {
    if (this.config.fixedWidthRem) {
      if (this.showCleanAction && !this.isViewOnly()) {
        return {'max-width': (this.config.fixedWidthRem - this.cleanActionWidthRem) + 'rem'};
      }
      return {'max-width': this.config.fixedWidthRem + 'rem'};
    }
    return {};
  }

  private onSelectChange(data: FormSelectOption | string | undefined): void {
    if (isDefined(data)) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        const oldValue: any = this.wrapper.form.controls[this.name].value;
        if (data instanceof FormSelectOption) {
          const newValue = data.value;
          this._onChangeValue(this.name, this.wrapper, newValue, oldValue);
          this.showCleanAction = nonBlank(newValue);
          this.accChanged.emit(data);
        } else if (this.type === 'input') {
          this._onChangeValue(this.name, this.wrapper, data, oldValue);
          this.showCleanAction = nonBlank(data);
        } else if (this.type === 'select' && isDefined(data)) {
          // Nothing to do: typing for filter
        } else {
          this.log.error('Typeahead select change with type "{}" not handled: {}', [this.type, data]);
        }
      }, 10);
    }
  }

  // see https://ng-bootstrap.github.io/#/components/typeahead/examples
  private buildSearch(): OperatorFunction<string, readonly any[]> {
    return (text$: Observable<string>) => {
      const debouncedText$: Observable<string> = text$.pipe(debounceTime(100), distinctUntilChanged());
      const clicksWithClosedPopup$: Observable<string> = this.clickTypeahead.pipe(filter(() => !this.instance || !this.instance.isPopupOpen()));
      const action: Observable<string> = merge(debouncedText$, this.focusTypeahead, clicksWithClosedPopup$);
      // Async data function
      if (isDefined(this.isAsyncData) && isDefined(this.asyncDataFn)) {
        return action.pipe(
          debounceTime(100),
          distinctUntilChanged(),
          tap(() => (this.searching = true)),
          switchMap((term) => this.asyncDataFn!(term).pipe(catchError(() => of([])))),
          tap(() => (this.searching = false))
        );
      }
      // Sync data function
      return action.pipe(map((term) => this.filterData(term)), delay(200));
    };
  }

  private buildFormatter(): (item: any) => string {
    return (data: FormSelectOption) => {
      return data.hasTranslation() && this.i18n.hasTranslationKey(data.translation)
        ? this.i18n.translate(data.translation)
        : data.name;
    };
  }

  private filterData(term: string): FormSelectOption[] {
    if (!this.isAsyncData && isDefined(this.data)) {
      if (isBlank(term)) {
        return this.data;
      }
      const search: string = term.toLowerCase();
      return this.data.filter((i) => {
        if (i.hasTranslatedValue()) {
          return i.translatedValue!.toLowerCase().indexOf(search) > -1;
        }
        return i.name.toLowerCase().indexOf(search) > -1;
      });
    }
    return [];
  }

  protected getModelValue(): string | undefined {
    if (this.config.showTooltip && isDefined(this.model)) {
      if (typeof this.model === 'string') {
        return this.model;
      }
      if (this.model.hasTranslation()) {
        return this.i18n.translate(this.model.translation);
      }
      return this.model.name;
    }
    return undefined;
  }

  protected formatHtml(value: string): string {
    return nonBlank(value)
      ? value.replace('<p>', '').replace('</p>', ' ')
      : '';
  }
}
